import React, { useContext, useEffect, useRef, useState } from 'react';
import { SubscriberInfoOutDTO } from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import { useTranslation } from 'react-i18next';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgBellUnsubcribed from '@icons/bell.svg?react';
import SvgBellSubscribed from '@icons/bell-inverted.svg?react';
import { SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';

interface ComponentProps {
  customerId: string;
  applicationId: string;
  secured?: SecuredConfig;
}

const SubscribeComponent: React.FC<ComponentProps> = ({ customerId, applicationId, secured }) => {
  const [open, setOpen] = useState(false);
  const node = useRef(null);
  const { decorate } = useSecuredDecoration(secured);

  const [subscriberInfo, setSubscriberInfo] = useState<SubscriberInfoOutDTO>({
    amISubscribed: false,
    subscriberUsers: [],
  });
  const { cuGetSubscriberInfo, cuSetOrUnsetMeAsSubscriber } = useContext(ApiContext);

  useEffect(() => {
    cuGetSubscriberInfo(customerId, applicationId, data => {
      setSubscriberInfo(data);
    });
    document.addEventListener('mousedown', e => handleClick(e));
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const handleSubscribeOrUnsubscribe = () => {
    cuSetOrUnsetMeAsSubscriber(customerId, applicationId, () => {
      cuGetSubscriberInfo(customerId, applicationId, data => {
        setSubscriberInfo(data);
      });
    });
  };

  const handleClick = (e: MouseEvent) => {
    if (node.current?.contains(e.target)) {
      // inside click
      return;
    }
    // outside click
    setOpen(false);
  };

  const { t } = useTranslation('common');

  return (
    <>{decorate(
      <div className={`notification-wrapper ${open ? 'active' : ''}`} ref={node}>
        <RingilButton
          mode={'table'}
          size={'big'}
          icon={subscriberInfo.amISubscribed ? <SvgBellSubscribed /> : <SvgBellUnsubcribed />}
          onClick={() => setOpen(!open)}
        />
        <div className="notification-layout animated fadeInDown">
          <div>
            {!subscriberInfo.amISubscribed && (
              <>
                <h3>{t(`SubscribeComponent.notificationsAreOff`, `Notifications are off`)}</h3>
                <p>{t(`SubscribeComponent.youWonTReceiveAny`, `You won’t receive any emails or in app notifications regarding this expedition.`)}</p>
                <RingilButton
                  mode={'filled-rectangle'}
                  icon={<SvgBellUnsubcribed />}
                  label={t(`SubscribeComponent.turnNotificationsOn`, `Turn notifications on`)}
                  onClick={() => handleSubscribeOrUnsubscribe()}
                />
              </>
            )}
            {subscriberInfo.amISubscribed && (
              <RingilButton
                mode={'filled-rectangle'}
                icon={<SvgBellUnsubcribed />}
                label={t(`SubscribeComponent.turnNotificationsOff`, `Turn notifications off`)}
                onClick={() => handleSubscribeOrUnsubscribe()}
              />
            )}

            {subscriberInfo.subscriberUsers.length > 0 && (
              <>
                <h4>{t(`SubscribeComponent.otherUsersFollowing`, `Other users following this expedition`)}</h4>
                <ul className="list-user">
                  {subscriberInfo.subscriberUsers.map((user, userIndex) => {
                    return (
                      <li className="user-item" key={userIndex}>
                        <div className="user-item__icon">
                          <strong>{user.initials}</strong>
                        </div>
                        <div className="user-item__title">
                          <strong>{user.name}</strong>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>,
    )}</>
  );
};

export default SubscribeComponent;
